/**
 * One delayed page reload when the API gateway cannot serve the app.
 *
 * 502, 503 and 504 usually mean a worker or pod is restarting. Reloading
 * immediately tends to hit the same outage, and reloading on every failed
 * request would stampede a recovering server. The first error schedules a
 * single reload. Further errors inside the cooldown only explain that the
 * automatic reload already happened.
 */

export const GATEWAY_RELOAD_STATUSES = [502, 503, 504];
export const GATEWAY_RELOAD_DELAY_MS = 5000;
export const GATEWAY_RELOAD_COOLDOWN_MS = 90000;
export const GATEWAY_RELOAD_STORAGE_KEY = "gatewayErrorReloadAt";

const BANNER_ID = "gateway-reload-banner";

const MESSAGES = {
  fi: {
    reload: "Yhteys palveluun katkesi. Sivu ladataan uudelleen hetken kuluttua.",
    suppress:
      "Automaattinen uudelleenkäynnistys tehtiin äsken. Odota hetki ja lataa sivu uudelleen tarvittaessa.",
  },
  en: {
    reload: "The service is unavailable. The page will reload shortly.",
    suppress:
      "The page was just reloaded automatically. Wait a moment and refresh again if needed.",
  },
  sv: {
    reload: "Tjänsten svarar inte. Sidan laddas om om en stund.",
    suppress:
      "Sidan laddades nyss om automatiskt. Vänta en stund och ladda om sidan vid behov.",
  },
};

let reloadPending = false;

export function decideGatewayReload({ status, now, lastReloadAt, reloadPending: pending }) {
  if (!GATEWAY_RELOAD_STATUSES.includes(status)) {
    return { action: "ignore" };
  }
  if (pending) {
    return { action: "ignore" };
  }
  if (typeof lastReloadAt === "number" && now - lastReloadAt < GATEWAY_RELOAD_COOLDOWN_MS) {
    return { action: "suppress" };
  }
  return { action: "reload", delayMs: GATEWAY_RELOAD_DELAY_MS };
}

function readLanguage() {
  try {
    const lang = localStorage.getItem("language");
    if (lang === "en" || lang === "sv" || lang === "fi") {
      return lang;
    }
  } catch (err) {
    // Storage can throw in locked-down browsers. Finnish is the product default.
  }
  return "fi";
}

function readLastReloadAt(storage) {
  try {
    const raw = storage.getItem(GATEWAY_RELOAD_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (err) {
    return null;
  }
}

export function showGatewayBanner(kind) {
  if (typeof document === "undefined") {
    return;
  }
  const text = MESSAGES[readLanguage()][kind];
  if (!document.body) {
    return;
  }
  let banner = document.getElementById(BANNER_ID);
  if (!banner) {
    banner = document.createElement("div");
    banner.id = BANNER_ID;
    banner.setAttribute("role", "status");
    banner.setAttribute("aria-live", "polite");
    Object.assign(banner.style, {
      position: "fixed",
      top: "0",
      left: "0",
      right: "0",
      zIndex: "10000",
      padding: "12px 16px",
      background: "#302df6",
      color: "#fff",
      textAlign: "center",
      fontSize: "15px",
      lineHeight: "1.4",
    });
    document.body.appendChild(banner);
  }
  banner.textContent = text;
}

/**
 * @param {number|undefined} status HTTP status from the failed API response.
 * @param {object} [deps] Test seams for storage, clock, timer and reload.
 * @returns {{ action: "ignore"|"suppress"|"reload", delayMs?: number }}
 */
function browserSessionStorage() {
  try {
    return sessionStorage;
  } catch (err) {
    // Some browsers throw when storage is disabled. Callers still need a no-op store.
    return {
      getItem() {
        return null;
      },
      setItem() {},
    };
  }
}

export function handleGatewayStatus(status, deps = {}) {
  const storage = deps.storage || browserSessionStorage();
  const now = deps.now != null ? deps.now : Date.now();
  const schedule = deps.schedule || ((fn, ms) => setTimeout(fn, ms));
  const reload = deps.reload || (() => window.location.reload());
  const showBanner = deps.showBanner || showGatewayBanner;

  const decision = decideGatewayReload({
    status,
    now,
    lastReloadAt: readLastReloadAt(storage),
    reloadPending,
  });

  if (decision.action === "reload") {
    reloadPending = true;
    try {
      storage.setItem(GATEWAY_RELOAD_STORAGE_KEY, String(now));
    } catch (err) {
      // A missing timestamp only weakens the cross-reload guard. The in-memory
      // flag still stops a second timer in this page load.
    }
    // Schedule before the banner. A missing document.body must not cancel the reload.
    schedule(reload, decision.delayMs);
    try {
      showBanner("reload");
    } catch (err) {
      // The reload is already scheduled.
    }
  } else if (decision.action === "suppress") {
    try {
      showBanner("suppress");
    } catch (err) {
      // The cooldown still applies when the notice cannot be shown.
    }
  }

  return decision;
}

/** Test helper. A real reload wipes module state with the page. */
export function resetGatewayReloadForTests() {
  reloadPending = false;
}
